import {
    ForwardedRef,
    ReactElement,
    forwardRef,
    useEffect,
    useRef,
    useState,
    MutableRefObject,
    RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';

import { findNextFocusableElement, useEscapeToClose, useKeyboardNavigation } from '@hh.ru/magritte-common-keyboard';
import { useCloseByClickOutside } from '@hh.ru/magritte-common-mouse';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useNoBubbling } from '@hh.ru/magritte-common-use-no-bubbling';
import { useWhenFontLoaded } from '@hh.ru/magritte-common-use-when-font-loaded';
import { useIntersectionSubscription } from '@hh.ru/magritte-internal-drop-base/intersectionManager';
import { usePositionManager } from '@hh.ru/magritte-internal-drop-base/positionManager';
import type { DropBasePermanentProps } from '@hh.ru/magritte-internal-drop-base/types';

import styles from './styles.less';

// Этот тип описан отдельно, чтобы данный проп не просачивался в Drop и Tooltip
type DropMetricsForceUpdateRef = { forceDropMetricsUpdate?: RefObject<VoidFunction> | MutableRefObject<VoidFunction> };
type DropBaseComponentProps = DropBasePermanentProps & DropMetricsForceUpdateRef;

const DropBaseComponent = (
    {
        visible,
        children,
        arrowGuardDistance,
        activatorRef,
        preferredViewport,
        onClose,
        autoFocusWhenOpened = false,
        closeByClickOutside = true,
        animationTimeout,
        animationClassNames,
        guardDistanceToActivator = 8,
        guardDistanceToViewport = 24,
        minHeight = 200,
        role,
        widthEqualToActivator = false,
        allowShrinkHeightToFitIntoViewport = false,
        keyboardNavigationMode = null,
        ignoreIntersections = false,
        style = {},
        onAppear,
        onBeforeExit,
        onAfterExit,
        host,
        placement,
        forceDropMetricsUpdate,
        lockPlacement = false,
        ...props
    }: DropBaseComponentProps,
    ref: ForwardedRef<HTMLElement>
): ReactElement => {
    const dropNodeRef = useRef<HTMLDivElement>(null);
    const [renderPortal, setRenderPortal] = useState(false);
    const isVisibleDropDown = !!(renderPortal && visible);
    const [lockClickOutside, setLockClickOutside] = useState(true);

    useWhenFontLoaded(() => setRenderPortal(true));

    const { bestFitPlacementRect, resizeRefCallbackFactory } = usePositionManager(
        activatorRef,
        dropNodeRef,
        isVisibleDropDown,
        placement,
        preferredViewport,
        allowShrinkHeightToFitIntoViewport,
        widthEqualToActivator,
        arrowGuardDistance || {},
        guardDistanceToActivator,
        guardDistanceToViewport,
        minHeight,
        forceDropMetricsUpdate as MutableRefObject<VoidFunction>,
        lockPlacement
    );
    useIntersectionSubscription(activatorRef, isVisibleDropDown, ignoreIntersections, () => {
        onClose?.();
    });

    const escapeToCloseRef = useEscapeToClose(onClose);
    const keyboardNavigationRef = useKeyboardNavigation(
        keyboardNavigationMode !== null
            ? { enabled: isVisibleDropDown, mode: keyboardNavigationMode }
            : { enabled: false }
    );
    const dropNodeRefCallback = useMultipleRefs(ref, dropNodeRef, keyboardNavigationRef, escapeToCloseRef);

    useCloseByClickOutside(
        dropNodeRef,
        () => {
            if (isVisibleDropDown && !lockClickOutside && activatorRef.current !== document.activeElement) {
                onClose?.();
            }
        },
        closeByClickOutside
    );

    const eventHandlers = useNoBubbling(props);

    useEffect(() => {
        if (!isVisibleDropDown) {
            setLockClickOutside(true);
        }
    }, [isVisibleDropDown, setLockClickOutside]);

    useEffect(() => {
        if (autoFocusWhenOpened && dropNodeRef.current !== null && isVisibleDropDown && !lockClickOutside) {
            const nextFocusableElement = findNextFocusableElement(dropNodeRef.current, null, 'down', 'list');
            if (nextFocusableElement !== null) {
                nextFocusableElement.focus();
            } else {
                dropNodeRef.current.focus();
            }
        }
    }, [autoFocusWhenOpened, isVisibleDropDown, lockClickOutside]);

    return (
        <>
            {renderPortal &&
                createPortal(
                    <CSSTransition
                        in={visible}
                        onEntered={() => {
                            setLockClickOutside(false);
                            onAppear?.();
                        }}
                        onExited={onAfterExit}
                        onExit={onBeforeExit}
                        timeout={animationTimeout}
                        classNames={animationClassNames}
                        unmountOnExit
                        appear
                        nodeRef={dropNodeRef}
                    >
                        <div
                            {...props}
                            {...eventHandlers}
                            data-qa="drop-base"
                            tabIndex={autoFocusWhenOpened ? 0 : -1}
                            className={styles.dropBase}
                            ref={dropNodeRefCallback}
                            style={{
                                ...style,
                                ...(bestFitPlacementRect || {}),
                            }}
                            role={role}
                        >
                            {children(resizeRefCallbackFactory)}
                        </div>
                    </CSSTransition>,
                    host?.current ?? document.body
                )}
        </>
    );
};

const DropBase = forwardRef<HTMLElement, DropBaseComponentProps>(DropBaseComponent);

export { DropBase };
